/** @jsxImportSource @emotion/react */

import { ApolloError, useMutation } from '@apollo/client'
import { omit } from 'lodash'
import { findOneUser_findOneUser as User } from '__generatedTypes__/findOneUser'
import { UPDATE_USER_MEMBERSHIP_BY_ADMIN } from 'queries/users'
import {
  updateUserMembershipByAdmin,
  updateUserMembershipByAdminVariables,
} from '__generatedTypes__/updateUserMembershipByAdmin'
import { toast } from 'utils/toast'
import { FormValues } from './ItemsManagement'

type Args = {
  user: User
}

export const usePauseTokenUpdate = ({ user }: Args) => {
  const [updateUserMembershipByAdmin] = useMutation<updateUserMembershipByAdmin, updateUserMembershipByAdminVariables>(
    UPDATE_USER_MEMBERSHIP_BY_ADMIN
  )

  const { id: userId, membership } = user

  const handleUpdateUserError = async (error: unknown) => {
    if (error instanceof Error) {
      if ('graphQLErrors' in error) {
        const gQLErr = error.graphQLErrors as ApolloError['graphQLErrors']
        if (gQLErr[0]?.message == 'Membership data is outdated, please try again') {
          toast.error('Membership data has been updated since the page was loaded. Reloading... Please try again')
        } else {
          toast.error('Failed to update tokens')
        }
      } else {
        toast.error('Failed to update tokens')
      }
    } else {
      toast.error('Failed to update tokens')
    }
  }

  const handleUpdateTokensCount = async (values: FormValues) => {
    try {
      const membershipToUpdate = membership.find((membership) => membership.platform.title === values.platform.title)

      if (!membershipToUpdate) return

      const omittedMembershipFields = omit(membershipToUpdate, '__typename', 'platform')

      await updateUserMembershipByAdmin({
        variables: {
          updateUserMembershipByAdminInput: {
            ...omittedMembershipFields,
            user: {
              id: userId,
            },
            pauseTokensCount: Number(values.itemCount),
          },
          membershipUpdatedAt: new Date(user.membershipUpdatedAt ?? 0).toISOString(),
        },
      })
    } catch (error) {
      handleUpdateUserError(error)
    }
  }

  return {
    handleUpdateTokensCount,
  }
}
